import type { ReactNode } from 'react'

import { useEffect, useRef } from 'react'

import { gsap } from '@/lib/gsap'

interface RevealGroupProps {
    children: ReactNode
    className?: string
    /** CSS selector for the children to stagger. Defaults to direct children. */
    childSelector?: string
    stagger?: number
    duration?: number
    y?: number
    ease?: string
    start?: string
}

export function RevealGroup({
    children,
    className,
    childSelector,
    stagger = 0.08,
    duration = 0.7,
    y = 24,
    ease = 'power2.out',
    start = 'top 85%'
}: RevealGroupProps) {
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const el = ref.current

        if (!el) return

        const targets = childSelector
            ? Array.from(el.querySelectorAll<HTMLElement>(childSelector))
            : Array.from(el.children) as HTMLElement[]

        if (!targets.length) return

        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        if (reduce) return

        const ctx = gsap.context(() => {
            gsap.set(targets, { opacity: 0, y })
            gsap.to(targets, {
                opacity: 1,
                y: 0,
                duration,
                ease,
                stagger,
                clearProps: 'transform',
                scrollTrigger: { trigger: el, start, once: true }
            })
        }, el)

        return () => ctx.revert()
    }, [childSelector, stagger, duration, y, ease, start])

    return (
        <div ref={ref} className={className}>
            {children}
        </div>
    )
}
